import { Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Layout components
import AppLayout from "./components/layouts/AppLayout";

// Page components

import EmployeeManagement from "./pages/EmployeeManagement";
import DepartmentHeadEmployeesPage from "./pages/employee/DepartmentHeadEmployeesPage";
import UpdateShiftsPage from "./pages/UpdateShiftsPage";
import OvertimeRequests from "./pages/OvertimeRequests";
import MyRequests from "./pages/MyRequests"; 
import Audit from "./pages/Audit";
import Reports from "./pages/Reports";
import Settings from "./pages/Settings";
import Companies from "./pages/Companies";
import ImpersonateUsers from "./pages/ImpersonateUsers";
import RequestDetails from "./pages/RequestDetails";
import DutyRosterManagementPage from "./pages/DutyRosterManagement"; 

import PendingRequests from "./pages/PendingRequests"; 
import ApproveRequest from "./pages/ApproveRequest";
import RejectRequest from "./pages/RejectRequest";

import Index from "./pages/Index";

// Auth components
import Login from "./pages/auth/Login";
import CompleteInvitePage from "./pages/auth/complete-invite/CompleteInvitePage";

// Register import removed as registration functionality is disabled
import { AuthProvider } from "./contexts/auth"; 
import { AuthGuard } from "./components/auth/AuthGuard";
import { UserRole as UserRoleEnum } from "./types/enums";

// Error components
import NotFound from "./components/common/NotFound";

// Overtime context
import { OvertimeProvider } from "./contexts/overtime";

// Employees context
import { EmployeesProvider } from "./contexts/employees";

// Create a client with optimized configuration for duty roster performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - increased for better caching
      gcTime: 30 * 60 * 1000, // 30 minutes (garbage collection time)
      refetchOnWindowFocus: false, // Disabled to prevent unnecessary refetches
      refetchOnMount: false, // Use cached data when available
      retry: 1,
      retryDelay: 1000, // 1 second delay between retries
    },
  },
});

// Only log in non-test environment (placeholder retained for compatibility)
// const shouldLog = process.env.NODE_ENV !== 'test';

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Toaster position="bottom-right" richColors />
        <Routes>
          {/* Public routes - no auth required */}
          <Route path="/login" element={
            <AuthGuard requireAuth={false}>
              <Login />
            </AuthGuard>
          } />
          
          {/* Complete invite route for invited users - auto-login and redirect to dashboard */}
          <Route path="/auth/complete-invite" element={<CompleteInvitePage />} />

          {/* Register route removed as registration functionality is disabled */}
          
          {/* Protected routes - require authentication */}
          <Route 
            path="/" 
            element={
              <AuthGuard>
                <EmployeesProvider>
                  <OvertimeProvider>
                    <AppLayout />
                  </OvertimeProvider>
                </EmployeesProvider>
              </AuthGuard>
            }
          >
            <Route index element={<Index />} />

            <Route path="request/:requestId" element={<RequestDetails />} />
            <Route path="approve/:requestId" element={
              <AuthGuard allowedRoles={[UserRoleEnum.DEPARTMENT_HEAD, UserRoleEnum.PLANT_MANAGER, UserRoleEnum.HR, UserRoleEnum.SUPER_ADMIN]}>
                <ApproveRequest />
              </AuthGuard>
            } />
            <Route path="reject/:requestId" element={
              <AuthGuard allowedRoles={[UserRoleEnum.DEPARTMENT_HEAD, UserRoleEnum.PLANT_MANAGER, UserRoleEnum.HR, UserRoleEnum.SUPER_ADMIN]}>
                <RejectRequest />
              </AuthGuard>
            } />

            <Route path="my-requests" element={
              <AuthGuard allowedRoles={[UserRoleEnum.EMPLOYEE, UserRoleEnum.DEPARTMENT_HEAD, UserRoleEnum.SUPERINTENDENT, UserRoleEnum.PLANT_MANAGER, UserRoleEnum.HR]}>
                <MyRequests />
              </AuthGuard>
            } />
            <Route path="pending-requests" element={<PendingRequests />} />

            
            {/* Routes requiring specific roles */}
            <Route path="employees" element={
              <AuthGuard allowedRoles={[UserRoleEnum.HR, UserRoleEnum.SUPER_ADMIN, UserRoleEnum.APP_OWNER]}>
                <EmployeeManagement />
              </AuthGuard>
            } />
            <Route path="department-employees" element={
              <AuthGuard allowedRoles={[UserRoleEnum.DEPARTMENT_HEAD, UserRoleEnum.SUPERINTENDENT]}>
                <DepartmentHeadEmployeesPage />
              </AuthGuard>
            } />
            <Route path="update-shifts" element={
              <AuthGuard allowedRoles={[UserRoleEnum.HR, UserRoleEnum.SUPER_ADMIN]}>
                <UpdateShiftsPage />
              </AuthGuard>
            } />
            <Route path="overtime-requests" element={<OvertimeRequests />} />
            <Route path="audit" element={<Audit />} />
            <Route path="reports" element={
              <AuthGuard allowedRoles={[UserRoleEnum.HR, UserRoleEnum.SUPER_ADMIN]}>
                <Reports />
              </AuthGuard>
            } />
            <Route path="duty-roster" element={
              <AuthGuard allowedRoles={[UserRoleEnum.HR, UserRoleEnum.SUPER_ADMIN]}>
                <DutyRosterManagementPage />
              </AuthGuard>
            } />
            <Route path="settings" element={<Settings />} />
            
            {/* App owner specific routes */}
            <Route path="companies" element={
              <AuthGuard allowedRoles={[UserRoleEnum.APP_OWNER]}>
                <Companies />
              </AuthGuard>
            } />
            <Route path="impersonate" element={
              <AuthGuard allowedRoles={[UserRoleEnum.APP_OWNER]}>
                <ImpersonateUsers />
              </AuthGuard>
            } />

          </Route>
          
          {/* Catch-all route for 404 handling */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;
